import React, { useState, useEffect } from 'react';

const CONTENT_TEMPLATES = {
  images: {
    authenticated: true,
    images: [
      { title: "Image 1", url: "/path/to/image1.jpg" },
      { title: "Image 2", url: "/path/to/image2.jpg" }
    ]
  },
  videos: {
    authenticated: true,
    videos: [
      { title: "Video 1", url: "/path/to/video1.mp4" },
      { title: "Video 2", url: "/path/to/video2.mp4" }
    ]
  },
  audio: {
    authenticated: true,
    audio: [
      { title: "Song 1", url: "/path/to/song1.mp3" },
      { title: "Song 2", url: "/path/to/song2.mp3" }
    ]
  },
  youtube: {
    authenticated: true,
    youTubeList: [
      { title: "Video Title", videoId: "dQw4w9WgXcQ" }
    ]
  },
  htmlDoc: {
    authenticated: true,
    htmlDoc: "<h1>HTML Content</h1><p>Your HTML content here</p>"
  },
  serverFolder: {
    authenticated: true,
    serverFolder: "../jaemzware.com/media/foldername"
  },
  insertForm: {
    authenticated: true,
    showInsertForm: true
  },
  deleteForm: {
    authenticated: true,
    showDeleteForm: true
  }
};

function AdminPanel({ baseDomainUrl }) {
  const [passwords, setPasswords] = useState([]);
  const [newPassword, setNewPassword] = useState("");
  const [newPayload, setNewPayload] = useState("");
  const [contentType, setContentType] = useState("images");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingPassword, setEditingPassword] = useState(null);

  useEffect(() => {
    fetchPasswords();
    const template = CONTENT_TEMPLATES['images'];
    setNewPayload(JSON.stringify(template, null, 2));
  }, []);

  const handleContentTypeChange = (type) => {
    setContentType(type);
    const template = CONTENT_TEMPLATES[type];
    setNewPayload(JSON.stringify(template, null, 2));
  };

  const handleCopyPassword = (password) => {
    setNewPassword(password + "_copy");
    setNewPayload(password.payload);
    setEditingPassword(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditPassword = (password) => {
    setEditingPassword(password.password);
    setNewPassword(password.password);
    setNewPayload(password.payload);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClear = () => {
    setEditingPassword(null);
    setNewPassword("");
    const template = CONTENT_TEMPLATES['images'];
    setNewPayload(JSON.stringify(template, null, 2));
    setContentType("images");
  };

  const fetchPasswords = async () => {
    try {
      const response = await fetch(baseDomainUrl + 'passwords');
      const data = await response.json();
      setPasswords(data);
      setLoading(false);
    } catch (err) {
      setError("Failed to load passwords: " + err.message);
      setLoading(false);
    }
  };

  const handleAddPassword = async () => {
    if (!newPassword || !newPayload) {
      alert("Password and payload are required");
      return;
    }

    try {
      JSON.parse(newPayload);
    } catch (err) {
      alert("Invalid JSON: " + err.message);
      return;
    }

    try {
      if (editingPassword && editingPassword !== newPassword) {
        await fetch(baseDomainUrl + 'password/?credential=' + encodeURIComponent(editingPassword), {
          method: 'DELETE'
        });
      }

      const response = await fetch(baseDomainUrl + 'password/?credential=' + encodeURIComponent(newPassword), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: newPayload
      });

      if (response.ok) {
        handleClear();
        fetchPasswords();
        alert(editingPassword ? "Password updated successfully" : "Password added successfully");
      } else {
        alert("Failed to save password");
      }
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const handleDeletePassword = async (pwd) => {
    if (!window.confirm(`Delete password "${pwd}"?`)) return;

    try {
      const response = await fetch(baseDomainUrl + 'password/?credential=' + encodeURIComponent(pwd), {
        method: 'DELETE'
      });

      if (response.ok) {
        fetchPasswords();
        alert("Password deleted");
      } else {
        alert("Failed to delete password");
      }
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  if (loading) return <div style={{ padding: '20px' }}>Loading...</div>;

  return (
    <div style={styles.container}>
      <h1>Password Manager</h1>

      <div style={styles.section}>
        <h2>Existing Passwords</h2>
        {error && <div style={styles.error}>{error}</div>}
        <table style={styles.table}>
          <thead>
            <tr>
              <th>Password</th>
              <th>Payload Preview</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {passwords.map((pwd) => (
              <tr key={pwd.password}>
                <td><code>{pwd.password}</code></td>
                <td style={styles.payloadCell}>
                  <code>{pwd.payload.substring(0, 100)}...</code>
                </td>
                <td style={styles.actionCell}>
                  <button
                    onClick={() => handleEditPassword(pwd)}
                    style={styles.editBtn}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleCopyPassword(pwd)}
                    style={styles.copyBtn}
                  >
                    Copy
                  </button>
                  <button
                    onClick={() => handleDeletePassword(pwd.password)}
                    style={styles.deleteBtn}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={styles.section}>
        <h2>{editingPassword ? `Editing: ${editingPassword}` : "Add New Password"}</h2>
        <div style={styles.form}>
          <input
            type="text"
            placeholder="Password (e.g., aoc)"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            style={styles.input}
          />

          <div>
            <label style={styles.label}>Content Type:</label>
            <select
              value={contentType}
              onChange={(e) => handleContentTypeChange(e.target.value)}
              style={styles.select}
            >
              <option value="images">Images Gallery</option>
              <option value="videos">Videos</option>
              <option value="audio">Audio/Music</option>
              <option value="youtube">YouTube Videos</option>
              <option value="htmlDoc">HTML Document</option>
              <option value="serverFolder">Server Folder Listing</option>
              <option value="insertForm">Insert Form</option>
              <option value="deleteForm">Delete Form</option>
            </select>
          </div>

          <textarea
            placeholder='Payload JSON'
            value={newPayload}
            onChange={(e) => setNewPayload(e.target.value)}
            style={styles.textarea}
          />
          <div style={styles.buttonGroup}>
            <button
              onClick={handleAddPassword}
              style={styles.addBtn}
            >
              {editingPassword ? "Update Password" : "Add Password"}
            </button>
            {editingPassword && (
              <button
                onClick={handleClear}
                style={styles.clearBtn}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
  },
  section: {
    marginBottom: '30px',
    borderBottom: '1px solid #ccc',
    paddingBottom: '20px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '20px',
  },
  payloadCell: {
    maxWidth: '400px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  actionCell: {
    display: 'flex',
    gap: '5px',
    flexWrap: 'wrap',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  buttonGroup: {
    display: 'flex',
    gap: '10px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '5px',
  },
  input: {
    padding: '10px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
  },
  select: {
    padding: '10px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    width: '100%',
  },
  textarea: {
    padding: '10px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    minHeight: '150px',
    fontFamily: 'monospace',
  },
  addBtn: {
    padding: '10px 20px',
    fontSize: '16px',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  editBtn: {
    padding: '5px 10px',
    fontSize: '12px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  copyBtn: {
    padding: '5px 10px',
    fontSize: '12px',
    backgroundColor: '#FF9800',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  deleteBtn: {
    padding: '5px 10px',
    fontSize: '12px',
    backgroundColor: '#f44336',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  clearBtn: {
    padding: '10px 20px',
    fontSize: '16px',
    backgroundColor: '#999',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  error: {
    color: 'red',
    marginBottom: '10px',
  },
};

export default AdminPanel;
